import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, ShoppingBag, Heart, User } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../config";
import AnnouncementBar from "./AnnouncementBar";

const NAV = [
  { label: "Accueil", to: "/" },
  { label: "Boutique", to: "/boutique" },
  { label: "Nouveautés", to: "/nouveautes" },
  { label: "Promotions", to: "/promotions" },
  { label: "Ventes Privées", to: "/ventes-privees" },
  { label: "À Propos", to: "/a-propos" },
  { label: "Contact", to: "/contact" },
];

export const Header = () => {
  const { count, setOpen, wishlist } = useCart();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/boutique?search=${encodeURIComponent(q.trim())}`); setSearchOpen(false); setQ(""); }
  };

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <AnnouncementBar />
      <div className={`transition-colors duration-300 backdrop-blur-xl border-b border-dore/20 ${scrolled ? "bg-creme/90" : "bg-creme/70"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 md:h-20">
          <button className="lg:hidden text-encre" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open" aria-label="Menu">
            <Menu size={24} />
          </button>

          <Link to="/" className="flex flex-col leading-none lg:mr-6 shrink-0" data-testid="logo-link">
            <span className="font-display text-lg md:text-2xl tracking-wide text-encre">La Boutique <span className="text-dore">d'Esther</span></span>
            <span className="hidden md:block font-serif-el italic text-xs text-ardoise/70 tracking-wide">{BRAND.slogan}</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
                className="text-[13px] uppercase tracking-[0.12em] text-encre/80 hover:text-dore transition-colors relative group">
                {n.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-dore transition-all duration-300 group-hover:w-full" />
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-5">
            <button onClick={() => setSearchOpen((s) => !s)} className="text-encre hover:text-dore transition-colors" data-testid="search-toggle" aria-label="Recherche">
              <Search size={20} />
            </button>
            <Link to="/boutique" className="hidden sm:block text-encre hover:text-dore transition-colors relative" data-testid="wishlist-link" aria-label="Favoris">
              <Heart size={20} />
              {wishlist.length > 0 && <span className="absolute -top-2 -right-2 bg-rose text-encre text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{wishlist.length}</span>}
            </Link>
            <Link to="/compte" className="hidden sm:block text-encre hover:text-dore transition-colors" data-testid="account-btn" aria-label="Compte">
              {user?.picture ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full object-cover" /> : <User size={20} className={user ? "text-dore" : ""} />}
            </Link>
            <button onClick={() => setOpen(true)} className="text-encre hover:text-dore transition-colors relative" data-testid="cart-toggle" aria-label="Panier">
              <ShoppingBag size={20} />
              {count > 0 && <span className="absolute -top-2 -right-2 bg-dore text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center" data-testid="cart-count">{count}</span>}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-dore/20 bg-creme/95 px-4 py-3">
            <form onSubmit={submitSearch} className="max-w-2xl mx-auto flex items-center gap-2">
              <Search size={18} className="text-ardoise" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"
                placeholder="Rechercher une robe, un bijou..." className="flex-1 bg-transparent outline-none text-sm py-2" />
            </form>
          </div>
        )}
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-4/5 max-w-xs bg-creme p-6 flex flex-col">
            <div className="flex justify-between items-center mb-8">
              <span className="font-display text-xl text-encre">Menu</span>
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close"><X size={24} /></button>
            </div>
            <nav className="flex flex-col gap-1">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
                  className="py-3 border-b border-dore/10 text-encre uppercase text-sm tracking-widest hover:text-dore transition-colors">
                  {n.label}
                </Link>
              ))}
              <Link to="/compte" onClick={() => setMobileOpen(false)} className="py-3 border-b border-dore/10 text-dore uppercase text-sm tracking-widest">Mon Compte</Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
